// 고객 대화(현장 하나에 한 줄기) — 표 모양은 `0037` 의 project_message·project_message_read 와 같다.

import type { OrgScopedRow } from "../common/types";
import type { PhotoMimeType, ProjectStatus } from "../project/types";

export const MESSAGE_AUTHOR_KINDS = ["STAFF", "CUSTOMER"] as const;
export type MessageAuthorKind = (typeof MESSAGE_AUTHOR_KINDS)[number];

// `0037` project_message_body_len 과 같은 자(앞뒤 공백을 뺀 글자 수).
export const MAX_MESSAGE_BODY = 2000;

// `0037` project_message_author_name_len 과 같은 자.
export const MAX_AUTHOR_NAME = 100;

// 자기 글만 고치고 지우기는 흔적만 남긴다 — updated_at 대신 edited_at·deleted_at(`0037`·`0120`).
export interface ProjectMessage extends Omit<OrgScopedRow, "updated_at"> {
  project_id: string;
  author_kind: MessageAuthorKind;
  author_member_id: string | null;
  author_name: string;
  body: string;
  edited_at: string | null;
  deleted_at: string | null;
  // 사진 한 장(`0120` project_message_photo_check — 셋이 같이 있거나 같이 없다).
  photo_path: string | null;
  photo_mime: PhotoMimeType | null;
  photo_size_bytes: number | null;
  // 사진 바이트가 저장소에 있는 걸 확인한 시각(`0120` — 올리기 성공·고아 청소만 찍는다).
  photo_checked_at: string | null;
  // 고아 청소가 바이트 「없음」을 처음 본 시각(`0120` — 24시간 뒤에도 없을 때만 치운다 · 다시 보이면 비운다).
  photo_missing_at: string | null;
}

// 화면이 그리는 글 — 저장 경로·형식·크기 대신 사진이 있는지만(사진은 라우트 주소로만 읽는다).
export type MessageItem = Omit<ProjectMessage, "photo_path" | "photo_mime" | "photo_size_bytes" | "photo_checked_at" | "photo_missing_at"> & { has_photo: boolean };

export interface MessagePhoto {
  path: string;
  mime: PhotoMimeType;
  size_bytes: number;
}

// 글을 고치거나 지우려는 쪽 — 직원은 자기 멤버 id, 고객은 링크 하나가 곧 본인이다.
export type MessageViewer = { kind: "STAFF"; member_id: string } | { kind: "CUSTOMER" };

export interface ProjectMessageRead extends OrgScopedRow {
  project_id: string;
  side: MessageAuthorKind;
  read_at: string;
}

// 화면·알림이 한 번에 쓰는 대화 한 줄기 — 읽은 시각은 「마지막으로 본 글의 created_at」(없으면 null).
export interface MessageThread {
  org_id: string;
  project_id: string;
  project_title: string;
  project_status: ProjectStatus;
  customer_name: string;
  messages: ProjectMessage[];
  staff_read_at: string | null;
  customer_read_at: string | null;
}

// 토큰이 가리키는 현장 자리 — 사진 경로·한도 열쇠·완료 확인에만 쓰는 가벼운 조회.
export type MessagePlace = Pick<MessageThread, "org_id" | "project_id" | "project_status">;

export interface MessageInput {
  id: string;
  body: string;
  photo?: MessagePhoto | null;
}

// 지우기 결과 — 저장소 밖에서 바이트를 치우도록 지우기 전 사진 경로를 같이 준다.
export interface RemovedMessage {
  message: ProjectMessage;
  dropped_photo_path: string | null;
}

// 지웠지만 사진 바이트가 남은 글 — 청소(cron·지우기 직후)가 바이트를 지우고 경로를 비운다.
export interface DeletedMessagePhoto {
  org_id: string;
  id: string;
  photo_path: string;
}

// 고아 청소가 바이트를 확인할 살아 있는 사진 글(아직 확인 표시가 없는 것).
export interface LiveMessagePhoto {
  org_id: string;
  project_id: string;
  id: string;
  photo_path: string;
  created_at: string;
  body: string;
  photo_missing_at: string | null;
}

// 고객 글 보내기의 결과 — 알림 제목 하나 때문에 대화를 다시 읽지 않는다.
export interface SharePostedMessage {
  message: ProjectMessage;
  project_title: string;
}
